import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ArticleListItem } from "@/types/api";
import { CategoryPill } from "@/components/news/CategoryPill";
import { FreshnessBadge } from "@/components/news/FreshnessBadge";
import { articleLeadImage, VideoCardBadge } from "@/components/news/VideoCardBadge";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { cn } from "@/lib/utils";
import { isWithin48h } from "@/lib/freshness";

const ROTATE_MS = 10_000;
const FLIP_MS = 760;
const SWIPE_PX = 48;

type FlipDirection = 1 | -1;

interface PageFlip {
  from: number;
  direction: FlipDirection;
  token: number;
}

interface Latest48hModuleProps {
  articles: ArticleListItem[];
  loading?: boolean;
}

export function Latest48hModuleSkeleton() {
  return (
    <section className="overflow-hidden rounded border border-brand-outline-variant/50 bg-brand-surface-lowest" aria-hidden>
      <div className="flex items-center gap-2 border-b border-brand-outline-variant/40 bg-brand-chrome px-4 py-2.5">
        <div className="h-3 w-40 animate-pulse rounded bg-white/30" />
      </div>
      <div className="grid lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="aspect-[16/9] animate-pulse bg-brand-surface-container" />
          <div className="space-y-3 p-4 sm:p-5">
            <div className="h-5 w-28 animate-pulse rounded bg-brand-surface-container" />
            <div className="h-7 w-full animate-pulse rounded bg-brand-surface-container" />
            <div className="h-4 w-4/5 animate-pulse rounded bg-brand-surface-container-low" />
          </div>
        </div>
        <div className="divide-y divide-brand-outline-variant/40 border-t border-brand-outline-variant/40 lg:border-l lg:border-t-0">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="min-h-[7.25rem] space-y-2 p-4">
              <div className="h-5 w-24 animate-pulse rounded bg-brand-surface-container" />
              <div className="h-4 w-full animate-pulse rounded bg-brand-surface-container" />
              <div className="h-4 w-3/4 animate-pulse rounded bg-brand-surface-container" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function secondaryArticles(articles: ArticleListItem[], activeIndex: number, limit = 3) {
  if (articles.length <= 1) return [];
  const rest: ArticleListItem[] = [];
  for (let offset = 1; offset < articles.length && rest.length < limit; offset += 1) {
    rest.push(articles[(activeIndex + offset) % articles.length]);
  }
  return rest;
}

function Latest48hGrid({
  articles,
  index,
  live,
  onPick,
}: {
  articles: ArticleListItem[];
  index: number;
  live: boolean;
  onPick?: (index: number) => void;
}) {
  const hero = articles[index % articles.length];
  const secondary = secondaryArticles(articles, index % articles.length);
  const image = articleLeadImage(hero);

  return (
    <div className={cn("grid lg:grid-cols-3", !live && "h-full")}>
      <article
        className="lg:col-span-2"
        aria-live={live ? "polite" : undefined}
        aria-atomic={live ? "true" : undefined}
      >
        <Link
          to={`/materia/${hero.slug}`}
          className={cn("group block", live && "latest-48h-fade")}
          tabIndex={live ? undefined : -1}
        >
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-brand-surface-container">
            {image ? (
              <OptimizedImage
                src={image}
                alt={hero.title}
                aspectRatio="16 / 9"
                priority={live && index === 0}
                sizes="(max-width: 1024px) 100vw, 820px"
                width={640}
                height={360}
                wrapperClassName="absolute inset-0 h-full w-full rounded-none"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-2xl font-bold text-brand-primary">
                Eusebio News
              </div>
            )}
            <div className="absolute left-3 top-3">
              <FreshnessBadge article={hero} fallbackRelative />
            </div>
            {hero.has_video ? <VideoCardBadge /> : null}
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            <CategoryPill category={hero.category} />
            <h3 className="line-clamp-3 min-h-[2.75em] font-display text-balance text-fluid-xl font-bold leading-tight text-brand-ink group-hover:text-brand-primary">
              {hero.title}
            </h3>
            <p className="line-clamp-3 min-h-[3.75em] font-serif text-sm leading-relaxed text-brand-on-surface-variant">
              {hero.summary || "\u00a0"}
            </p>
          </div>
        </Link>
      </article>

      <div className="divide-y divide-brand-outline-variant/40 border-t border-brand-outline-variant/40 lg:border-l lg:border-t-0">
        {secondary.map((article) => (
          <article key={article.id} className="flex min-h-[7.25rem] flex-col justify-center p-4">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <FreshnessBadge article={article} fallbackRelative />
              <CategoryPill category={article.category} className="px-2 py-0.5 text-[10px]" />
            </div>
            <Link
              to={`/materia/${article.slug}`}
              tabIndex={live ? undefined : -1}
              className="line-clamp-3 font-display text-base font-semibold leading-snug text-brand-ink hover:text-brand-primary"
              onClick={() => {
                if (!live || !onPick) return;
                const nextIndex = articles.findIndex((item) => item.id === article.id);
                if (nextIndex >= 0) onPick(nextIndex);
              }}
            >
              {article.title}
            </Link>
          </article>
        ))}
      </div>
    </div>
  );
}

export function Latest48hModule({ articles, loading }: Latest48hModuleProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fadeKey, setFadeKey] = useState(0);
  const [flip, setFlip] = useState<PageFlip | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const indexRef = useRef(0);
  const flipTimer = useRef<number | null>(null);
  const flipSeq = useRef(0);
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  const clearFlipTimer = () => {
    if (flipTimer.current !== null) {
      window.clearTimeout(flipTimer.current);
      flipTimer.current = null;
    }
  };

  const beginFlip = useCallback((from: number, direction: FlipDirection) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setFlip(null);
      return;
    }
    flipSeq.current += 1;
    setFlip({ from, direction, token: flipSeq.current });
    clearFlipTimer();
    flipTimer.current = window.setTimeout(() => setFlip(null), FLIP_MS);
  }, []);

  const goBy = useCallback(
    (step: FlipDirection) => {
      const length = articles.length;
      if (length < 2) return;
      const from = ((indexRef.current % length) + length) % length;
      const next = (from + step + length) % length;
      beginFlip(from, step);
      indexRef.current = next;
      setActiveIndex(next);
      setFadeKey((key) => key + 1);
    },
    [articles.length, beginFlip],
  );

  const goTo = useCallback(
    (index: number) => {
      const length = articles.length;
      if (length < 2) return;
      const from = ((indexRef.current % length) + length) % length;
      if (index === from) return;
      beginFlip(from, index > from ? 1 : -1);
      indexRef.current = index;
      setActiveIndex(index);
      setFadeKey((key) => key + 1);
    },
    [articles.length, beginFlip],
  );

  useEffect(() => {
    indexRef.current = 0;
    setActiveIndex(0);
    setFadeKey((key) => key + 1);
    setFlip(null);
    clearFlipTimer();
  }, [articles]);

  useEffect(() => () => clearFlipTimer(), []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(media.matches);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (articles.length < 2 || paused || tabHidden || reducedMotion) return;

    const timer = window.setInterval(() => goBy(1), ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [articles.length, paused, tabHidden, reducedMotion, goBy]);

  useEffect(() => {
    if (!paused || articles.length < 2) return;

    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (!sectionRef.current?.contains(target) && !sectionRef.current?.matches(":hover")) return;
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goBy(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        goBy(-1);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paused, articles.length, goBy]);

  if (loading) return <Latest48hModuleSkeleton />;
  if (!articles.length) return null;

  const currentIndex = ((activeIndex % articles.length) + articles.length) % articles.length;
  const hasFresh = articles.some((article) => isWithin48h(article));
  const canRotate = articles.length > 1;
  const showFlip = Boolean(flip) && !reducedMotion;

  return (
    <section
      ref={sectionRef}
      className={cn(
        "overflow-hidden rounded border border-brand-outline-variant/50 bg-brand-surface-lowest",
        tabHidden && "latest-48h-hold",
      )}
      aria-labelledby="latest-48h-heading"
      aria-roledescription={canRotate ? "carrossel" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div className="flex items-center justify-between gap-3 border-b border-brand-outline-variant/40 bg-brand-chrome px-4 py-2.5 text-white">
        <div className="flex min-w-0 items-center gap-2">
          <span className="freshness-pulse h-2 w-2 shrink-0 rounded-full bg-amber-400" aria-hidden />
          <h2 id="latest-48h-heading" className="font-display text-xs font-bold uppercase tracking-wider">
            {hasFresh ? "Ultimas 48 horas no Eusebio" : "Em destaque no Eusebio"}
          </h2>
        </div>
        {canRotate ? (
          <div className="flex items-center gap-1.5" aria-label="Slides do carrossel">
            {articles.map((article, index) => (
              <button
                key={article.id}
                type="button"
                aria-label={`Mostrar matéria ${index + 1}: ${article.title}`}
                aria-current={index === currentIndex ? "true" : undefined}
                className={cn(
                  "relative h-1.5 rounded-full transition-all duration-300 before:absolute before:-inset-x-1.5 before:-inset-y-3 before:content-['']",
                  index === currentIndex ? "w-5 bg-amber-400" : "w-1.5 bg-white/45 hover:bg-white/70",
                )}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Palco do page flip. O slide novo usa .latest-48h-flip-in (rotateY)
          e o Link interno mantém .latest-48h-fade. O slide anterior sai
          com .latest-48h-flip-out. Swipe horizontal troca o slide. */}
      <div
        className="latest-48h-stage"
        onTouchStart={(event) => {
          const touch = event.changedTouches[0];
          swipeRef.current = { x: touch.clientX, y: touch.clientY };
        }}
        onTouchEnd={(event) => {
          const start = swipeRef.current;
          swipeRef.current = null;
          if (!start || !canRotate) return;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - start.x;
          const dy = touch.clientY - start.y;
          if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy)) return;
          goBy(dx < 0 ? 1 : -1);
        }}
        onTouchCancel={() => {
          swipeRef.current = null;
        }}
      >
        {showFlip && flip ? (
          <div
            key={flip.token}
            className={cn("latest-48h-flip-out", flip.direction < 0 && "latest-48h-flip-out--back")}
            aria-hidden="true"
          >
            <Latest48hGrid articles={articles} index={flip.from} live={false} />
          </div>
        ) : null}
        <div
          key={fadeKey}
          className={cn(
            showFlip && "latest-48h-flip-in",
            showFlip && flip && flip.direction < 0 && "latest-48h-flip-in--back",
          )}
        >
          <Latest48hGrid articles={articles} index={currentIndex} live onPick={goTo} />
        </div>
      </div>
    </section>
  );
}
